import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Drawer from '@material-ui/core/Drawer';
import Button from 'eventtia-ui-components/lib/Button';
import Badge from '@material-ui/core/Badge';
import IconButton from '@material-ui/core/IconButton';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Tooltip from 'eventtia-ui-components/lib/Tooltip';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import clsx from 'clsx';
// import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import LogOutIcon from '../../assets/sidebarIcons/LogOut';
import { logout, setActiveSubpage } from '../../actions/app';
import subpages, { buildCustomSubpage } from '../../helpers/subpages';
import getVisibleWorkshops from '../../helpers/workshops';
import {
  getCurrentAttendee,
  getCurrentParticipant,
  getAvailableModules,
  getModuleByType,
} from '../../helpers/getters';
import { canChat } from '../../helpers/chat';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import useRoutes from '../../hooks/useRoutes';
import useParams from '../../hooks/useParams'; // VS-PT-HACK

const useSidebarButtonStyles = makeStyles((theme) => ({
  mobileMenuItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:nth-child(even)': {
      backgroundColor: theme.palette.lightGrey.light,
    },
  },
  activeMobileMenuItem: {
    color: theme.palette.primary.main,
  },
  mobileMenuItemText: {
    marginLeft: theme.spacing(8),
    textTransform: 'uppercase',
    fontWeight: 400,
    justifyContent: 'flex-start',
    fontSize: 14,
    margin: theme.spacing(1, 0),
    color: theme.palette.common.black,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  activeMenuItem: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.navbar.main,
    '& svg': {
      color: theme.palette.navbar.main,
    },
    '&:hover': {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      fontWeight: 700,
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
      '& svg': {
        color: theme.palette.primary.main,
      },
    },
  },
  menuItem: {
    color: theme.palette.darkGrey.dark,
  },
  moduleName: {
    marginLeft: theme.spacing(3),
  },
  tooltipTitle: {
    fontSize: 16,
  },
  desktopMenuContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    color: theme.palette.lightGrey.main,
  },
  collapsedIconWrapper: {
    width: theme.spacing(8),
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  openedDesktopMenu: {
    display: 'flex',
    width: '100%',
    padding: 0,
    overflowX: 'hidden',
    whiteSpace: 'nowrap',
    '&$activeMenuItem': {
      backgroundColor: 'transparent',
    },
  },
  expandedButtonBase: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    minHeight: 48,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '$activeMenuItem &': {
      backgroundColor: theme.palette.primary.main,
      borderTopLeftRadius: 24,
      borderBottomLeftRadius: 24,
      marginLeft: theme.spacing(1),
      width: `calc(100% - ${theme.spacing(1)}px)`,
    },
  },
  iconContainer: {
    width: 64,
    minWidth: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.main,
    '$activeMenuItem &': {
      width: 48,
      minWidth: 48,
    },
  },
  expandedLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    textTransform: 'uppercase',
    fontWeight: 400,
    fontSize: 14,
    fontFamily: 'Brilliant Cut Pro',
    color: theme.palette.common.black,
    '$activeMenuItem &': {
      color: theme.palette.lightGrey.main,
    },
  },
}));

const SidebarButton = ({
  text, active, mobile, isHovered, ...other
}) => {
  const classes = useSidebarButtonStyles();
  return mobile ? (
    <div className={clsx(classes.mobileMenuItem, active && classes.activeMobileMenuItem)}>
      <Button
        aria-label={text}
        variant="tertiary"
        className={clsx(classes.mobileMenuItemText, active && classes.activeMenuItem)}
        icon={other.children}
        {...other}
      >
        <div className={classes.moduleName}>{text}</div>
      </Button>
    </div>
  ) : (
    <div className={classes.desktopMenuContainer}>
      {!isHovered ? (
        <div className={classes.collapsedIconWrapper}>
          <Tooltip
            title={<div className={classes.tooltipTitle}>{text}</div>}
            placement="right"
          >
            <IconButton
              className={clsx(classes.menuItem, active && classes.activeMenuItem)}
              aria-label={text}
              {...other}
            />
          </Tooltip>
        </div>
      ) : (
        <div className={clsx(classes.openedDesktopMenu, active && classes.activeMenuItem)}>
          <ButtonBase
            aria-label={text}
            className={classes.expandedButtonBase}
            onClick={other.onClick}
            disableRipple
          >
            <div className={classes.iconContainer}>
              {other.children}
            </div>
            <div className={classes.expandedLabelContainer}>
              <span>{text}</span>
            </div>
          </ButtonBase>
        </div>
      )}
    </div>
  );
};

SidebarButton.propTypes = {
  mobile: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  active: PropTypes.bool,
  isHovered: PropTypes.bool.isRequired,
};

SidebarButton.defaultProps = {
  active: false,
};

const SubpageButtonBase = ({
  subpage: { names, name, Icon, type }, setActiveSubpage: dispatchSetActiveSubpage,
  activeSubpage, showBadge, closeMobileSidebar, onClick, isHovered,
}) => {
  const { t } = useTranslation('sidebar');
  const { locale } = useParams(); // VS-PT-HACK
  const theme = useTheme();
  const defaultModuleText = names ? names[locale] : t(`subpages.${name}`);
  const showMobileNavbar = useMediaQuery(theme.breakpoints.down('sm'));

  const text = type === 'Custom' ? name : defaultModuleText;
  return (
    <SidebarButton
      onClick={() => {
        if (onClick) onClick();
        else dispatchSetActiveSubpage({ module: name });
        if (showMobileNavbar && closeMobileSidebar) closeMobileSidebar();
      }}
      text={text}
      active={name === activeSubpage.module}
      mobile={showMobileNavbar}
      isHovered={isHovered}
    >
      <Badge color="secondary" variant="dot" overlap="circle" invisible={!showBadge}>
        {typeof Icon === 'string' ? (
          <img src={Icon} alt="icon" style={{ height: 16 }} />
        ) : (
          <Icon />
        )}
      </Badge>
    </SidebarButton>
  );
};

SubpageButtonBase.propTypes = {
  closeMobileSidebar: PropTypes.func,
  onClick: PropTypes.func,
  subpage: PropTypes.shape({
    names: PropTypes.shape({
      es: PropTypes.string,
      en: PropTypes.string,
      fr: PropTypes.string,
    }),
    name: PropTypes.string,
    Icon: PropTypes.elementType,
    showBadge: PropTypes.func,
    type: PropTypes.string,
  }).isRequired,
  setActiveSubpage: PropTypes.func.isRequired,
  activeSubpage: PropTypes.shape({
    module: PropTypes.string,
  }).isRequired,
  showBadge: PropTypes.bool.isRequired,
  isHovered: PropTypes.bool.isRequired,
};

SubpageButtonBase.defaultProps = {
  onClick: undefined,
  closeMobileSidebar: undefined,
};

const mapStateToProps = (state, { subpage: { showBadge } }) => ({
  activeSubpage: state.app.activeSubpage,
  showBadge: !!showBadge?.(state),
});
const SubpageButton = connect(mapStateToProps, { setActiveSubpage })(SubpageButtonBase);

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    width: theme.spacing(8),
    zIndex: theme.zIndex.modal + 1,
    border: 'none',
    transition: 'width 0.2s ease-in-out',
  },
  expandedRoot: {
    width: 330,
    transition: 'width 0.2s ease-in-out',
    willChange: 'width',
  },
  mobileRoot: {
    height: '100%',
    width: '100%',
    zIndex: theme.zIndex.modal + 1,
  },
  paper: {
    position: 'absolute',
    overflow: 'hidden',
    backgroundColor: theme.palette.navbar.main,
    color: theme.palette.darkGrey.light,
    boxShadow: theme.customShadows.small,
    zIndex: 1500,
    '& .simplebar-scrollbar:before': {
      background: 'white',
    },
  },
  paperHovered: {
    background: 'linear-gradient(to right, white 64px, rgba(255, 255, 255, 0.7) 64px)',
  },
  simplebar: {
    overflowX: 'hidden',
    maxHeight: '100vh',
  },
  content: {
    padding: theme.spacing(13, 0, 4),
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',
    '& > button:last-of-type, & > div:last-of-type': {
      marginTop: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      marginTop: theme.spacing(5),
      alignItems: 'flex-start',
      gap: 0,
    },
  },
  expandedDesktopMenu: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minHeight: '100vh',
  },
  title: {
    marginLeft: theme.spacing(8),
    marginBottom: theme.spacing(4),
    fontFamily: 'Brilliant Cut Pro',
    fontSize: 14,
    fontWeight: 700,
  },
}));

const buildDefaultSubpage = (module) => ({
  ...subpages[module.type],
  names: module.name,
  id: module.id,
});

const Sidebar = ({
  logout: dispatchLogout, entities, appSettings, sponsors, open, onClose, activeSubpage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('sidebar');
  const theme = useTheme();
  const { locale } = useParams();
  const [settings] = Object.values(appSettings);
  const { modules } = settings;
  const currentAttendee = getCurrentAttendee(entities);
  const currentParticipant = getCurrentParticipant(entities);
  const activeModules = ['Workshops', 'Attendees', 'Custom', 'AttendeeProfile', 'Sponsors', 'Surveys', 'Speakers', 'SocialWall', 'AttendeeLeads', 'QASessions', 'Quizzes'];
  const { visibleWorkshops } = getVisibleWorkshops(entities, false);
  const availableModules = getAvailableModules(modules, currentAttendee.attendeeType.id,
    locale, activeModules, currentParticipant, visibleWorkshops);
  const doubleSponsors = getModuleByType(modules, 'Sponsors');

  const modulesToShow = availableModules.filter(({ type }) => (
    !['AttendeeProfile'].includes(type)
  )).map((module) => {
    if (module.type === 'Custom') return buildCustomSubpage(module, locale, currentAttendee);
    return buildDefaultSubpage(module);
  });

  const homeModule = getModuleByType(modules, 'Home');
  const { goTo } = useRoutes();
  const goToLanding = () => {
    if (activeSubpage.module !== 'Home') goTo('home', {}, { module: 'Home' });
  };

  const goToMainStage = () => {
    goTo('stage');
  };

  const path = window.location.pathname.split('/');
  // this will explode if an event has home as its eventuri.
  // in order to avoid the problem, we created an event with home as its eventUri. EZ.
  const isInHome = path.includes('home');
  const mainStageShouldAlsoRedirect = isInHome || activeSubpage.module === 'Home';

  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chatModule = modules?.find(({ type }) => type === 'Chat');
  const channels = chatModule?.customParams?.channels || {};

  const availableChatChannel = Object.values(channels).find(({ availableFor }) => (
    availableFor.includes(currentAttendee.attendeeType.id)
  ));
  const chatEnabled = canChat(settings, currentAttendee.attendeeType.id) || availableChatChannel;

  const myProfileModule = availableModules.find(({ type }) => type === 'AttendeeProfile');

  const [isHovered, setIsHovered] = useState(false);

  let sidebarClass;
  if (mobile) sidebarClass = classes.mobileRoot;
  else if (isHovered) sidebarClass = classes.expandedRoot;
  else sidebarClass = classes.root;

  return (
    <Drawer
      classes={{
        paper: clsx(
          sidebarClass, classes.paper, isHovered && !mobile && classes.paperHovered
        ),
      }}
      variant={mobile ? 'temporary' : 'permanent'}
      open={open}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SimpleBar className={classes.simplebar}>
        <div className={clsx(classes.content, isHovered && classes.expandedDesktopMenu)}>
          {mobile && (
            <Typography className={classes.title} variant="subtitle1">
              WORLD MEETING 20.<span style={{ color: '#C6A265' }}>26</span>
            </Typography>
          )}
          {myProfileModule && (
            <>
              <SubpageButton
                subpage={{
                  ...subpages.AttendeeProfile,
                  names: myProfileModule.name,
                }}
                closeMobileSidebar={onClose}
                isHovered={isHovered}
              />
            </>
          )}
          {/* HACK */}
          {false && homeModule?.enabled && (
            <SubpageButton
              subpage={buildDefaultSubpage(homeModule)}
              onClick={goToLanding}
              closeMobileSidebar={onClose}
              isHovered={isHovered}
            />
          )}
          {false && (
            <SubpageButton
              subpage={homeModule?.enabled ? subpages.mainStage : subpages.homelessMainStage}
              onClick={mainStageShouldAlsoRedirect ? goToMainStage : undefined}
              closeMobileSidebar={onClose}
              isHovered={isHovered}
            />
          )}
          {modulesToShow.map((module) => (
            <SubpageButton
              key={module.id}
              subpage={module}
              closeMobileSidebar={onClose}
              isHovered={isHovered}
            />
          ))}
          {!doubleSponsors && Object.keys(sponsors).length > 0 && (
            // eslint-disable-next-line max-len
            <SubpageButton subpage={subpages.Sponsors} closeMobileSidebar={onClose} isHovered={isHovered} />
          )}
          {chatEnabled && (
            <SubpageButton
              subpage={{
                ...subpages.Messages,
                names: chatModule?.name,
              }}
              closeMobileSidebar={onClose}
              isHovered={isHovered}
            />
          )}
          {/* <SubpageButton
            subpage={subpages.settings}
            closeMobileSidebar={onClose}
          /> */}
          <SidebarButton
            onClick={() => {
              dispatchLogout();
              if (mobile && onClose) onClose();
            }}
            text={t('actions.logOut')}
            mobile={mobile}
            isHovered={isHovered}
          >
            <LogOutIcon />
          </SidebarButton>
        </div>
      </SimpleBar>
    </Drawer>
  );
};

const mapStateToSidebarProps = ({
  entities,
  entities: { appSettings, sponsors },
  app: { activeSubpage },
}) => ({
  entities,
  appSettings,
  sponsors,
  activeSubpage,
});

Sidebar.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  logout: PropTypes.func.isRequired,
  entities: CustomPropTypes.entities.isRequired,
  appSettings: PropTypes.objectOf(CustomPropTypes.appSettings).isRequired,
  sponsors: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ),
  activeSubpage: CustomPropTypes.activeSubpage.isRequired,
};

Sidebar.defaultProps = {
  sponsors: {},
  open: undefined,
  onClose: undefined,
};

export default connect(mapStateToSidebarProps, { logout })(Sidebar);
